import React, {
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react';
import { ModalNextProvider, Link } from '@jutro/components';
import { SgSptRegionHelperModal } from 'sg-spt-modals';
import { SgDateUtil, SgPersonUtil, SgSptTripUtils } from 'saga-modules';
import _ from 'lodash';
import SgSptConstants from 'sg-spt-constants';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { TranslatorContext } from '@jutro/locale';
import metadata from './SgSptRenewalSummaryInfo.metadata.json5';
import messages from './SgSptRenewalSummaryInfo.messages';

function SgSptRenewalSummaryInfo(props) {
    const {
        modelVM,
    } = props;

    const translator = useContext(TranslatorContext);
    const coverLevel = modelVM.renewalDetails.value.coverLevel.toLowerCase();
    const isAggOriginated = coverLevel.includes(SgSptConstants.COVER_LEVELS.SELECT);
    const travellers = modelVM.lobData.privateTravel.coverables.sptTravellers.children;
    const [showAllTravellers, setShowAllTravellers] = useState(travellers.length <= SgSptConstants.MAX_INITIAL_TRAVELLERS_NUMBER_ON_RENEWAL);
    const phIndex = modelVM.lobData.privateTravel.coverables.sptTravellers.value.findIndex((el) => el.isPolicyHolder);

    const fullNameWithTitle = SgPersonUtil.getFullNameWithTitle(
        travellers[phIndex].person.value
    );

    const showRegionHelperModal = useCallback((event) => {
        event.preventDefault();
        ModalNextProvider.showModal(
            <SgSptRegionHelperModal modelVM={modelVM} readOnlyContext />
        );
    }, [modelVM]);

    const winterSportsAdded = _.some(travellers, (tr) => _.get(tr, 'value.isWinterSportsNeeded', false));

    const winterSportsAddedTravellers = useMemo(() => {
        const winterSportsCustomers = [];
        travellers.forEach((traveller, travellerNo) => {
            if (traveller.isWinterSportsNeeded.value) {
                const travellerFullName = `${travellers[travellerNo].person.firstName.value} ${travellers[travellerNo].person.lastName.value}`;
                winterSportsCustomers.push(travellerFullName.split(' ').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' '));
            }
        });
        return winterSportsCustomers.join(', ');
    }, [travellers]);

    const { reducedTravellersNames, allTravellersNames } = useMemo(() => {
        if (travellers.length === 1) {
            return '';
        }
        const additionalTravellers = travellers.filter((traveller) => !traveller.value.isPolicyHolder);
        let reducedNames = '';
        for (let i = 0; i < 3 && i !== additionalTravellers.length; i += 1) {
            const { person } = additionalTravellers[i].value;
            if (i > 0) {
                reducedNames = `${reducedNames}, `;
            }
            reducedNames = `${reducedNames}${SgPersonUtil.getFullNameWithTitle(person)}`;
        }
        let allNames = `${reducedNames}`;
        if (travellers.length > SgSptConstants.MAX_INITIAL_TRAVELLERS_NUMBER_ON_RENEWAL) {
            for (let i = 3; i < additionalTravellers.length; i += 1) {
                const { person } = additionalTravellers[i].value;
                allNames = `${allNames}, ${SgPersonUtil.getFullNameWithTitle(person)}`;
            }
            reducedNames = `${reducedNames} +${travellers.length - 4}`;
        }

        return {
            reducedTravellersNames: reducedNames,
            allTravellersNames: allNames
        };
    }, [travellers]);

    const getTravellersContent = useCallback(() => {
        const toggleExpanded = (event) => {
            event.preventDefault();
            setShowAllTravellers(!showAllTravellers);
        };
        const content = (
            <>
                <div>
                    {showAllTravellers && allTravellersNames}
                    {!showAllTravellers && reducedTravellersNames}
                </div>
                {travellers.length > SgSptConstants.MAX_INITIAL_TRAVELLERS_NUMBER_ON_RENEWAL && (
                    <div>
                        <Link
                            href="/"
                            id="showMoreAdditionalTravellersLink"
                            onClick={toggleExpanded}
                        >
                            <span>
                                {showAllTravellers
                                    ? translator(messages.ShowLessTravellersLink)
                                    : translator(messages.ShowMoreTravellersLink)
                                }
                            </span>
                        </Link>
                    </div>
                )}
            </>
        );
        return content;
    }, [reducedTravellersNames, allTravellersNames, showAllTravellers, setShowAllTravellers, travellers]);

    const overrideProps = {
        renewalSummaryDescription: {
            content: modelVM.renewalDetails.value.description
        },
        renewalSummaryLeadName: {
            content: fullNameWithTitle
        },
        additionalTravellersLabelContainer: {
            visible: travellers.length > 1 ? '' : false
        },
        renewalSummaryAdditionalTravellers: {
            content: getTravellersContent()
        },
        renewalSummaryCoverName: {
            content: isAggOriginated
                ? translator(messages.SelectCoverLevel)
                : modelVM.renewalDetails.value.coverLevel.split('-')[0]
        },
        renewalSummaryStartDate: {
            content: SgDateUtil.getDateWithFullMonthName(modelVM.renewalDetails.value.periodStart)
        },
        renewalSummaryRenewalDate: {
            content: SgDateUtil.getDateWithFullMonthName(modelVM.renewalDetails.value.periodEnd)
        },
        renewalSummaryRegion: {
            content: modelVM.renewalDetails.value.coverLevel.split('-')[1]
        },
        additionalCoverLabelContainer: {
            visible: winterSportsAdded
        },
        renewalSummaryAdditionalCover: {
            content: translator(messages.WinterSportsCover, {
                winterSportsTraveller: winterSportsAddedTravellers,
            })
        },
        SptRenewalSummaryRegionHelperLink: {
            visible: SgSptTripUtils.isSelectedTripAnnual(modelVM),
            onClick: showRegionHelperModal
        }
    };
    return renderContentFromMetadata(metadata.componentContent, overrideProps);
}

export default SgSptRenewalSummaryInfo;
